import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fontSize, MediumTitle, wh } from '../../mixins';
import {
  selectLandingType,
  selectPlan,
  setPlan,
  setDefaultPlanId,
} from './plansSlice';
import { OrrangeCircle, WhiteCircle } from './icons';
import LatamPlan from './LatamPlan';
import OneTimePlan from './OneTimePlan';
import PackagePlans from './PackagePlans';
import PackageOptions from './PackageOptions';

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ customPadding }) => customPadding || '70px 0 0'};
`;

const StyledTitle = styled.b`
  ${MediumTitle};
  margin-bottom: 24px;
`;

const StyledIcon = styled.span`
  position: absolute;
  top: 33%;
  right: 6%;
`;

const StyledList = styled.ul`
  width: 100%;
  max-width: 345px;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const StyledName = styled.span`
  ${fontSize(14)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  font-weight: 500;
`;

const StyledPrice = styled.span`
  ${fontSize(12)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  color: #fff;

  &:first-letter {
    text-transform: uppercase;
  }
`;

const StyledLi = styled.li`
  width: 100%;
  position: relative;
  background: linear-gradient(97.26deg, #454545 0%, #303030 100.55%);
  box-shadow: inset 0 0 0 1px
    ${({ active }) => (active ? '#FBA253' : 'transparent')};
  box-sizing: border-box;
  border-radius: 8px;
  margin-bottom: 12px;

  &:first-child {
    border-radius: 0 0 8px 8px;
    margin-top: 20px;

    &::before {
      content: attr(data-value);
      width: 100%;
      height: 20px;
      color: #fff;
      font-weight: 600;
      ${fontSize(10)};
      line-height: 1;
      text-transform: uppercase;
      background-color: #f2994a;
      border-radius: 8px 8px 0 0;
      padding: 5px 16px;
      box-sizing: border-box;
      position: absolute;
      top: -20px;
      left: 0;
    }
  }

  button {
    width: 100%;
    height: 100%;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: transparent;
    border: none;
    padding: 11px 16px;
    cursor: pointer;

    &:focus {
      outline: none;
    }
  }
`;

const StyledOptions = styled.ul`
  max-width: 345px;
  list-style: none;
  width: 100%;
  background: #454545;
  border-radius: 12px;
  padding: 7px 16px 7px 65px;
`;

const StyledOption = styled.li`
  position: relative;
  ${fontSize(14)};
  line-height: 22px;
  letter-spacing: -0.225882px;
  color: #ffffff;
  padding: 10px 0;

  &:before {
    content: '';
    width: 20px;
    height: 20px;
    background-image: url('../assets/orrange-circle.svg');
    background-repeat: no-repeat;
    background-size: contain;
    position: absolute;
    left: -34px;
  }
`;

const StyledButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: sticky;
  bottom: 24px;
  left: 0;

  ${({ landingType, isSticked }) =>
    landingType !== 'optionsh' &&
    `
        &:before {
            content: '';
            display: block;
            width: 100vw;
            height: 258px;
            position: absolute;
            left: calc(-50vw + 50%);
            top: -72px;
            background: linear-gradient(0deg,#005f52 0%,#005f52 64%,rgba(0,95,82,0.35) 90%,rgba(0,95,82,0) 100%);
            pointer-events: none;
            transition: opacity .3s; 
            opacity: ${isSticked ? 1 : 0};
        }
    `}
`;

const InvisFlag = styled.div`
  ${wh('1px')};
  position: absolute;
  bottom: -25px;
  left: 0;
`;

const CustomButton = styled.div`
  ${wh('90%', 'auto')};
  max-width: 327px;
  background: #f09a51;
  color: #ffffff;
  ${fontSize(14)};
  line-height: 18px;
  font-weight: 500;
  padding: 14px 20px;
  border-radius: 24px;
  position: relative;
  text-align: center;
`;

const StyledOptionsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin: 20px 0 25px 20px;
`;

const StyledPromoPlan = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(92.51deg, #ef7a12 18.45%, #fba253 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  padding: 12px 0 14px;
  color: #fff;
  font-weight: 800;
  ${fontSize(30)};
  line-height: 1;
  letter-spacing: -0.460567px;
  position: relative;

  &:before {
    content: '';
    display: block;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    position: absolute;
    top: -1px;
    left: -1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.4)
    );
    border-radius: 17px;
    z-index: -1;
  }

  span:last-child {
    ${fontSize(16)};
    font-weight: normal;
    margin-top: 8px;

    &:first-letter {
      text-transform: uppercase;
    }
  }
`;

const StyledPromoTitle = styled.div`
  font-weight: 600;
  ${fontSize(24)};
  line-height: 100%;
  color: #fff;
  letter-spacing: -0.460567px;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 24px;
`;

const ExperimentalPlans = ({ onBuyClick }) => {
  const { t } = useTranslation();
  const plan = useSelector(selectPlan);
  const dispatch = useDispatch();
  const landingType = useSelector(selectLandingType);
  const packagePlans = ['optionsg', 'optionsh'];
  const promoPlans = ['promo', 'trial'];
  const isPackage = packagePlans.includes(landingType);
  const isPromoPlans = promoPlans.includes(landingType);
  const isLatam = landingType === 'latam';
  const isOneTimePlan = landingType === 'optionsj';
  const [isSticked, setIsSticked] = useState(null);
  const flagRef = useRef(null);

  useEffect(() => {
    const ref = flagRef.current;

    const observer = new IntersectionObserver(
      ([e]) => setIsSticked(e.intersectionRatio < 1),
      { threshold: [1] }
    );
    if (ref) {
      window.scrollTo(0, 0);
      observer.observe(ref);
    }

    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, []);

  const PLANS = useMemo(
    () => ({
      optionsa: [
        {
          id: 'gz-3-months-intro-banana',
          title: `3 ${t('months')}`,
          price: t('optionsa_price_1'),
          popular: true,
        },

        {
          id: 'gz-1-month-intro-franko',
          title: `1 ${t('week')}`,
          period: `${t('week')}`,
          price: `$9.99 ${t('per_week')}*`,
          note: `*${t('weekly_sub_note')}`,
        },
      ],
      optionsb: [
        {
          id: 'gz-1-month-intro-kobylanska',
          title: `1 ${t('month')}`,
          price: t('optionsb_price_1'),
          popular: true,
        },
        {
          id: 'gz-3-months-red-banana',
          title: `3 ${t('months')}`,
          price: `$3.33 / ${t('week')}`,
        },
      ],
      optionsd: [
        {
          id: 'gz-1-month-intro-vynnychenko',
          title: t('trial_offer'),
          price: t('optionsd_intro'),
          popular: true,
        },
        {
          id: 'gz-3-months-red-banana',
          title: `3 ${t('months')}`,
          price: `$3.33 / ${t('week')}`,
        },
      ],
      optionse: [
        {
          id: 'gz-1-month-tychyna',
          title: `1 ${t('month')}`,
          price: `$2.99 / ${t('week')}`,
          popular: true,
        },
        {
          id: 'gz-3-month-kostenko',
          title: `3 ${t('months')}`,
          price: `$3.33 / ${t('week')}`,
        },
      ],
      optionsf: [
        {
          id: 'gz-3-month-kostenko',
          title: `3 ${t('months')}`,
          price: `$3.33 / ${t('week')}`,
          popular: true,
        },
        {
          id: 'gz-1-month-tychyna',
          title: `1 ${t('month')}`,
          price: `$2.99 / ${t('week')}`,
        },
      ],
      intro: [
        {
          id: 'gz-28-days-vasyl-stus',
          title: t('free_trial_for_3_days'),
          price: `${t('then')} $7.99 ${t('week')}`,
          popular: true,
        },
        {
          id: 'gz-56-days-lesya-ukrainka',
          title: `2 ${t('months')}`,
          price: `$5.99 /${t('week')}`,
        },
      ],
      latam: [
        {
          id: 'gz-1-month-tychyna',
          title: `1 ${t('month')}`,
          oldPrice: '$19.99',
          price: '$11.97',
          pricePerWeek: '$2.99',
          bestseller: true,
          bestsellerDiscount: '-40%',
        },
        {
          id: 'gz-3-month-kostenko',
          title: `3 ${t('months')}`,
          oldPrice: '$49.99',
          price: '$39.95',
          pricePerWeek: '$9.98',
        },
      ],
      optionsg: [
        {
          id: 'gz-1-month-2999',
          title: t('premium'),
          period: `${t('week')}`,
          saveAmount: '50%',
          week: t('per_week'),
          prevPrice: '$14.99',
          weeklyPrice: '$7.49',
          price: '$29.99',
        },
        {
          id: 'gz-1-month-tomato',
          title: t('basic'),
          period: `${t('week')}`,
          week: t('per_week'),
          prevPrice: '$9.99',
          weeklyPrice: '$4.99',
          price: '$19.96',
        },
      ],
      optionsh: [
        {
          id: 'gz-1-month-tiger',
          title: t('premium'),
          period: `${t('week')}`,
          saveAmount: '50%',
          week: t('per_week'),
          prevPrice: '$14.99',
          weeklyPrice: '$9.99',
          price: '$39.96',
        },
        {
          id: 'gz-1-month-2999',
          title: t('basic'),
          period: `${t('week')}`,
          week: t('per_week'),
          prevPrice: '$9.99',
          weeklyPrice: '$7.49',
          price: '$29.99',
        },
      ],
      optionsj: [
        {
          id: 'gz-1-month-2999',
          title: `${t('unlock')}`,
          period: `${t('week')}`,
          week: t('per_week'),
          prevPrice: '$9.99',
          weeklyPrice: '$7.49',
          price: '$29.99',
        },
      ],
      promo: [
        {
          id: 'gz-monthly-free3d-2999',
          title: `3 ${t('3_days_free')}`,
          price: `${t('then')} $7.49 ${t('per_week')}`,
        },
      ],
      trial: [
        {
          id: 'gz-monthly-free3d-2999',
          title: `3 ${t('3_days_free')}`,
          price: `${t('then')} $7.49 ${t('per_week')}`,
          bestseller: true,
        },
        {
          id: 'gz-3-months-red-banana',
          title: `3 ${t('months')}`,
          price: `$3.33 / ${t('week')}`,
        },
      ],
    }),
    [t]
  );

  const onPlanClick = (id) => dispatch(setPlan(id));

  const currentPlans = useMemo(() => PLANS[landingType], [landingType, PLANS]);

  useEffect(() => {
    dispatch(setPlan(currentPlans[0].id));
    dispatch(setDefaultPlanId(currentPlans[0].id));
  }, [dispatch, currentPlans]);

  const renderOffer = (item, i) => {
    const { id, title, price, note } = item;
    const isActive = plan === id;

    return (
      <React.Fragment key={id}>
        <StyledLi active={isActive} data-value={t('most_popular')}>
          <button onClick={() => onPlanClick(id)} id={`plan-button-${i + 1}`}>
            <StyledName>{title.toUpperCase()}</StyledName>
            <StyledPrice>{price}</StyledPrice>
            <StyledIcon>{isActive ? OrrangeCircle : WhiteCircle}</StyledIcon>
          </button>
        </StyledLi>
        {note &&
          (plan === 'gz-1-month-intro-franko' ? (
            <StyledPrice>{note}</StyledPrice>
          ) : (
            <StyledPrice>&nbsp;</StyledPrice>
          ))}
      </React.Fragment>
    );
  };

  const renderPromoPlan = (() => {
    const { title, price } = currentPlans[0];
    return (
      <StyledPromoPlan>
        <span>{title}</span>
        <span>{price}</span>
      </StyledPromoPlan>
    );
  })();

  const buttonWrapper = (
    <StyledButtonWrapper isSticked={isSticked} landingType={landingType}>
      <CustomButton onClick={onBuyClick} id={'continue-button'}>
        {t('continue')}
      </CustomButton>
      <InvisFlag ref={flagRef} />
    </StyledButtonWrapper>
  );

  if (isLatam) {
    return (
      <>
        <LatamPlan
          currentPlans={currentPlans}
          onPlanClick={onPlanClick}
          plan={plan}
        />
        {buttonWrapper}
      </>
    );
  }

  if (isOneTimePlan) {
    return (
      <>
        <OneTimePlan currentPlans={currentPlans} />
        {buttonWrapper}
      </>
    );
  }

  if (isPackage) {
    return (
      <>
        <PackagePlans
          currentPlans={currentPlans}
          onPlanClick={onPlanClick}
          plan={plan}
          landingType={landingType}
          isSticked={isSticked}
        />
        {buttonWrapper}
      </>
    );
  }

  if (isPromoPlans) {
    return (
      <>
        <StyledContainer customPadding={'10px 10px 0'}>
          <StyledPromoTitle>{t('unlock_all_features')}</StyledPromoTitle>
          {landingType === 'promo' ? (
            renderPromoPlan
          ) : (
            <StyledList>{currentPlans.map(renderOffer)}</StyledList>
          )}
          <StyledOptionsWrapper>
            <PackageOptions active promo />
          </StyledOptionsWrapper>
        </StyledContainer>
        {buttonWrapper}
      </>
    );
  }

  return (
    <>
      <StyledContainer>
        <StyledTitle>{t('unlock_all_access')}</StyledTitle>
        <StyledList>{currentPlans.map(renderOffer)}</StyledList>
        <StyledOptions>
          <StyledOption>{t('unlimited_place_alerts')}</StyledOption>
          <StyledOption>{t('unlimited_live_locating')}</StyledOption>
          <StyledOption>{t('location_history')}</StyledOption>
        </StyledOptions>
      </StyledContainer>
      {buttonWrapper}
    </>
  );
};

export default ExperimentalPlans;
